import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { cn } from '@repo/ui/lib/utils';
import { Mail, MapPin, Phone } from 'lucide-react';
import statusBadges from '@/assets/landing/status-badges.svg';
import ravenLogo from '@/assets/logo.png';

const getSolutions = () => [
  t('extra.smartKycKyb'),
  t('extra.reportGeneration'),
  t('extra.complianceInsights'),
  t('extra.teamCollaboration'),
];

const quickLinks = [
  {
    get label() {
      return t('workspace.home');
    },
    href: '#home',
  },
  {
    get label() {
      return t('landing.ourSolution');
    },
    href: '#our-solution',
  },
  {
    get label() {
      return t('landing.whoIsRavenFor');
    },
    href: '#who-is-raven-for',
  },
  {
    get label() {
      return t('landing.aboutUs');
    },
    href: '#about-us',
  },
  {
    get label() {
      return t('reports.contact');
    },
    href: '#contact',
  },
  {
    get label() {
      return t('landing.customerReviews');
    },
    href: '#reviews',
  },
];

export const LandingFooter = ({ className }: { className?: string }) => {
  useLocale();

  return (
    <footer className={cn('relative overflow-hidden', className)}>
      {/* Status badges decoration — single centered image */}
      <div
        className="pointer-events-none absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url(${statusBadges})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center bottom',
        }}
      />

      <div className="relative z-10 mx-auto max-w-6xl px-6 pt-16 pb-8 lg:px-8">
        <div className="mb-10 grid grid-cols-1 gap-12 md:grid-cols-5">
          {/* Column 1 – Brand */}
          <div className="md:col-span-3">
            <img alt={t('workspace.raven')} className="mb-3 h-8 w-auto" src={ravenLogo} />
            <p className="max-w-[200px] text-landing-muted text-xs leading-relaxed">
              {t(
                'landing.smartDueDiligencePlatformForModernOrganizationsAutomatedIntelligent',
              )}{' '}
            </p>

            <div className="mt-5 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-landing-muted text-xs">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                contact@raven.com
              </div>
              <div className="flex items-center gap-2 text-landing-muted text-xs">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                {t('landing.parisFrance')}{' '}
              </div>
              <div className="flex items-center gap-2 text-landing-muted text-xs">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                +1 (555) 555-5555
              </div>
            </div>
          </div>

          {/* Column 2 – Solutions */}
          <div>
            <h4 className="mb-4 font-libre-baskerville font-semibold text-landing-heading-sm text-sm">
              {t('landing.solutions')}{' '}
            </h4>
            <ul className="flex flex-col gap-2">
              {getSolutions().map((s) => (
                <li key={s}>
                  <a
                    className="text-landing-heading text-landing-muted text-xs transition hover:font-libre-baskerville"
                    href="#our-solution"
                  >
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 – Quick Links */}
          <div>
            <h4 className="mb-4 font-libre-baskerville font-semibold text-landing-heading-sm text-sm">
              {t('landing.quickLinks')}{' '}
            </h4>
            <ul className="flex flex-col gap-2">
              {quickLinks.map(({ label, href }) => (
                <li key={label}>
                  <a
                    className="text-landing-heading text-landing-muted text-xs transition hover:font-libre-baskerville"
                    href={href}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-landing-card-border border-t pt-6">
          <p className="text-center text-landing-muted text-xs">
            {t('landing.2026RavenAllRightsReserved')}{' '}
          </p>
        </div>
      </div>
    </footer>
  );
};
